/**
 * Bespoke hover controller for the "All Products" editorial mega menu.
 *
 * Scoped to a single nav trigger + panel pair (not the generic header-menu
 * activate/deactivate system) so it can match the design-handoff spec exactly:
 * open on mouseenter, stay open while the pointer is over the trigger OR the
 * panel, close 260ms after the pointer leaves both (cancelable), with a
 * CSS-driven entrance transition. This is the one piece of required JS per
 * the handoff — everything else is CSS.
 */
class MegaMenuPanel extends HTMLElement {
  static CLOSE_DELAY_MS = 260;

  /** @type {number | null} */
  _closeTimer = null;

  /** @type {HTMLElement | null} */
  _trigger = null;

  connectedCallback() {
    this._trigger = this.querySelector('[data-mega-menu-trigger]');

    this.addEventListener('mouseenter', this._open);
    this.addEventListener('mouseleave', this._scheduleClose);
    this.addEventListener('focusin', this._open);
    this.addEventListener('focusout', this._onFocusOut);
    this.addEventListener('keydown', this._onKeydown);
  }

  disconnectedCallback() {
    this._clearTimer();
  }

  _clearTimer = () => {
    if (this._closeTimer !== null) {
      window.clearTimeout(this._closeTimer);
      this._closeTimer = null;
    }
  };

  _open = () => {
    this._clearTimer();
    this.setAttribute('open', '');
    this._trigger?.setAttribute('aria-expanded', 'true');
  };

  _close = () => {
    this._closeTimer = null;
    this.removeAttribute('open');
    this._trigger?.setAttribute('aria-expanded', 'false');
  };

  _scheduleClose = () => {
    this._clearTimer();
    this._closeTimer = window.setTimeout(this._close, MegaMenuPanel.CLOSE_DELAY_MS);
  };

  /** @param {FocusEvent} event */
  _onFocusOut = (event) => {
    const nextTarget = event.relatedTarget;
    if (nextTarget instanceof Node && this.contains(nextTarget)) return;
    this._scheduleClose();
  };

  /** @param {KeyboardEvent} event */
  _onKeydown = (event) => {
    if (event.key === 'ArrowDown' && event.target === this._trigger) {
      event.preventDefault();
      this._open();
      this.querySelector('[data-mega-menu-panel] a[href]')?.focus();
      return;
    }
    if (event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    this._clearTimer();
    // Focusing the trigger fires focusin. Close after focus so Escape stays closed.
    this._trigger?.focus();
    this._close();
  };
}

if (!customElements.get('mega-menu-panel')) {
  customElements.define('mega-menu-panel', MegaMenuPanel);
}

